import moment from 'moment';
import * as api from '../api';
import { TOKEN_HEADER } from '../constants';
import { getJwtToken } from '../storage';

const getQueryWithDates = (query, from, until) => ({
  ...query,
  fromDate: from || moment().subtract(90, 'days').format('YYYY-MM-DD'),
  untilDate: until || moment().endOf('day').format('YYYY-MM-DD'),
});

export const getReclamations = async (args) => {
  const maxRetries = 3;
  let retries = 0;

  const {
    options: { siteId },
    data: {
      query: { fromDate, untilDate },
    },
  } = args;
  const query = getQueryWithDates(args.data.query, fromDate, untilDate);

  while (retries < maxRetries) {
    const response = await api.callGet(`/manager/sites/${siteId}/reclamations`, {
      ...args,
      data: { query },
    });

    const lastPage = Math.ceil(response.total / response.pageSize - 1);
    if (response.page != lastPage && response.results.length != response.pageSize) {
      retries++;
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } else {
      return response;
    }
  }
};

export const getClaims = (args) => {
  const {
    options: { siteId },
    data: {
      query: { fromDate, untilDate },
    },
  } = args;
  const query = getQueryWithDates(args.data.query, fromDate, untilDate);
  return api.callGet(`/manager/sites/${siteId}/claims`, {
    ...args,
    data: { query },
  });
};
export const getClaimCounts = (args) => {
  const {
    options: { siteId },
  } = args;
  const _args = {
    ...args,
    options: { ...args.options },
  };
  return api.callGet(`/manager/sites/${siteId}/claimCounts`, _args);
};

export const getCarPartReports = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/claims/report/car-part`, args);
};

export const getCarMakeReports = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/claims/report/car-make`, args);
};

export const getCarModelReports = (args) => {
  const {
    options: { siteId },
    data: { makeId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/claims/report/car-make/${makeId}/models`, args);
};

export const patchReclamation = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/reclamations/${id}`, args);
};

export const postReclamation = (args) => {
  const { siteId } = args.options;
  return api.callPost(`/manager/sites/${siteId}/reclamations`, args);
};

export const deleteReclamation = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callDelete(`/manager/sites/${siteId}/reclamations/${id}`, args);
};

export const archiveReclamation = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/reclamations/${id}/archive`, args);
};

export const unarchiveReclamation = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/reclamations/${id}/unarchive`, args);
};

export const getReclamation = async (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  const response = await api.callGet(
    `/manager/sites/${siteId}/reclamations/${id}?includeHistory=true`,
    args
  );
  const normalized = response?.reclamation
    ? { ...response.reclamation, history: response.history }
    : response;
  return normalized;
};

export const postReclamationNote = (args) => {
  const {
    options: { siteId },
    data: { reclamationId, note },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/reclamations/${reclamationId}/notes`, {
    data: note,
    options: args.options,
  });
};

export const archiveReclamationNote = (args) => {
  const {
    options: { siteId },
    data: { reclamationId, noteId },
  } = args;
  return api.callPost(
    `/manager/sites/${siteId}/reclamations/${reclamationId}/notes/${noteId}/archive`,
    {
      options: args.options,
    }
  );
};

export const getClaimDamageOptions = (args) => {
  return api.callGet(`/manager/claims-damage-options`, args);
};

export const postClaimDamageOptions = (args) => {
  return api.callPost(`/manager/claims-damage-options`, args);
};

export const getClaimEmailTemplates = (args) => {
  return api.callGet('/manager/claim-email-templates', {
    ...args,
    options: withAuthenticatedOptions(args?.options),
  });
};

export const getClaimEmailTemplate = (args) => {
  const {
    data: { templateKey },
  } = args;
  return api.callGet(`/manager/claim-email-templates/${templateKey}`, {
    ...args,
    options: withAuthenticatedOptions(args?.options),
  });
};

export const sendReclamationEmail = (args) => {
  const {
    options,
    data: { id, siteId: dataSiteId, ...emailBody },
  } = args;
  const siteId = dataSiteId || options?.siteId;
  return api.callPost(`/manager/sites/${siteId}/reclamations/${id}/send-email`, {
    data: emailBody,
    options: {
      credentials: 'include',
      headers: {
        [TOKEN_HEADER]: getJwtToken(),
      },
    },
  });
};

const withAuthenticatedOptions = (options = {}) => ({
  ...options,
  credentials: 'include',
  headers: {
    [TOKEN_HEADER]: getJwtToken(),
    ...(options.headers || {}),
  },
});

export const getNotificationSettings = (args = {}) => {
  return api.callGet('/manager/notification-settings', {
    ...args,
    options: withAuthenticatedOptions(args.options),
  });
};

export const saveNotificationSettings = (args = {}) => {
  return api.callPost('/manager/notification-settings', {
    data: args.data,
    options: withAuthenticatedOptions(args.options),
  });
};
